import type { LegalDoc } from "./types";

// German (DSGVO). Übersetzung der Referenzrichtlinie (v1.0, 1. September 2026).
const gdprDe: LegalDoc = {
  title: "Datenschutzrichtlinie (GDPR / RGPD)",
  effectiveDate: "2. November 2026",
  version: "1.2",
  intro:
    "Diese Richtlinie erläutert, wie Photohawk personenbezogene Daten im Rahmen der Datenschutz-Grundverordnung der EU (GDPR / DSGVO) und ihres französischen Pendants, der RGPD (Règlement Général sur la Protection des Données), sowie der UK GDPR verarbeitet. RGPD und GDPR bezeichnen dasselbe Recht.",
  sections: [
    {
      heading: "1. Wer wir sind",
      blocks: [
        {
          type: "p",
          text: "Photohawk ist eine Plattform für Eventfotografie, die es Fotografen und Veranstaltern ermöglicht, Eventfotos zu veröffentlichen, zu durchsuchen und zu verkaufen.",
        },
        {
          type: "ul",
          items: [
            "Rechtsträger: CLOUDCONCUR LTD (Handelsregisternummer 13399913), eine in England und Wales eingetragene Gesellschaft, die unter dem Handelsnamen Photohawk tätig ist.",
            "Eingetragene Anschrift: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Vereinigtes Königreich.",
            "Datenschutzbeauftragter (DPO): Andrew Hammond — Kontakt support@photohawk.com.",
            "EU-Vertreter (GDPR Art. 27): Wir haben keinen EU-Vertreter benannt, da unsere Verarbeitung von Daten von EU-Ansässigen gelegentlich erfolgt und nicht unsere Haupttätigkeit darstellt (Art. 27(2)). Wir überprüfen dies regelmäßig und werden einen Vertreter benennen, sofern sich unsere Verarbeitung ändert.",
            "Aufsichtsbehörden: das Information Commissioner's Office (ICO) für das Vereinigte Königreich sowie EU-Behörden, darunter die französische CNIL, für Personen in der EU.",
          ],
        },
      ],
    },
    {
      heading: "2. Unsere zwei Rollen — Verantwortlicher und Auftragsverarbeiter",
      blocks: [
        {
          type: "p",
          text: "Photohawk hat mit zwei Arten von Personen zu tun, und unsere rechtliche Rolle unterscheidet sich für jede von ihnen:",
        },
        {
          type: "ul",
          items: [
            "Fotografen und Veranstalter (unsere Kunden). Für Ihre Kontodaten (Name, E-Mail, Geschäftsangaben, Login, Abrechnungs- und Auszahlungsinformationen) ist Photohawk der Verantwortliche.",
            "Eventteilnehmer und Käufer (Ihre Kunden). Für die Daten der auf den Fotos abgebildeten und diese kaufenden Personen sind Sie (der Fotograf/Veranstalter) der Verantwortliche und Photohawk Ihr Auftragsverarbeiter — wir verarbeiten diese Daten nach Ihren Weisungen, um die von Ihnen eingerichteten Galerien, die Suche und die Verkäufe zu betreiben. Ein Auftragsverarbeitungsvertrag (siehe §11) regelt dieses Verhältnis.",
          ],
        },
      ],
    },
    {
      heading: "3. Welche personenbezogenen Daten wir verarbeiten",
      blocks: [
        { type: "p", text: "Für Fotografen/Veranstalter (wir sind Verantwortlicher):" },
        {
          type: "ul",
          items: [
            "Identität und Kontakt: Name, E-Mail, Telefon, Firmenname, Anschrift.",
            "Konto und Sicherheit: Anmeldedaten (über AWS), Sitzungen.",
            "Finanzdaten: Abonnement-/Abrechnungsdaten (über Stripe) und Auszahlungsangaben (über Trolley).",
          ],
        },
        { type: "p", text: "Für Eventteilnehmer/Käufer (wir sind Auftragsverarbeiter, im Auftrag des Veranstalters):" },
        {
          type: "ul",
          items: [
            "Identität und Kontakt: Name, E-Mail, Telefonnummer, Post-/Rechnungsanschrift.",
            "Fotografien der Teilnehmer.",
            "Biometrische Daten — sofern die Gesichts-/Selfie-Suche aktiviert ist (siehe §4).",
            "Bestell- und Zahlungsdaten (verarbeitet über unsere Zahlungsdienstleister; wir speichern keine vollständigen Kartennummern).",
            "Status der Marketing-Einwilligung, sofern der Teilnehmer zustimmt.",
          ],
        },
      ],
    },
    {
      heading: "4. Gesichts- und Selfie-Suche (biometrische Daten besonderer Kategorien)",
      blocks: [
        {
          type: "p",
          text: "Wenn ein Veranstalter die Selfie-/Gesichtssuche aktiviert, erstellt Photohawk eine mathematische Darstellung von Gesichtern, damit ein Teilnehmer seine eigenen Fotos finden kann.",
        },
        {
          type: "p",
          text: "Die Gesichtssuche ist optional. Wir speichern Gesichtsdaten (numerische Gesichtsvorlagen, keine Fotos) so lange, wie der Administrator der Galerie es festlegt, standardmäßig bis die Galerie, das Foto oder die Gesichtsdaten gelöscht werden. Wir löschen sie endgültig, wenn der gewählte Zeitraum abläuft, wenn die Galerie oder das Foto gelöscht wird, innerhalb von 30 Tagen, nachdem die Gesichtssuche für die Galerie ausgeschaltet wurde, innerhalb von 24 Stunden, nachdem der Workspace des Administrators geschlossen wurde, oder innerhalb von 30 Tagen, wenn Sie uns um Löschung bitten oder Ihre Einwilligung widerrufen. Das Archivieren einer Galerie löscht ihre Gesichtsdaten nicht. Für die Suche hochgeladene Selfies werden nur für diese Suche verwendet und nicht gespeichert.",
        },
        {
          type: "link",
          text: "Alle Einzelheiten:",
          label: "Richtlinie zur Aufbewahrung biometrischer Daten",
          href: "/biometric-data-retention",
        },
        {
          type: "ul",
          items: [
            "Zweck: ausschließlich, um eine Person ihren eigenen Eventfotos zuzuordnen.",
            "Ort: verarbeitet und gespeichert in der Europäischen Union (AWS, Region Irland).",
            "Aufbewahrung: Gesichtsdaten werden für den Zeitraum aufbewahrt, den der Administrator der Galerie wählt (30, 90 oder 180 Tage oder 12 Monate nach dem Hochladen des Fotos, standardmäßig ohne festen Zeitraum), und gelöscht, wenn die Galerie oder das Foto gelöscht wird, innerhalb von 30 Tagen nach dem Ausschalten der Gesichtssuche für die Galerie, innerhalb von 24 Stunden nach dem Schließen des Workspace und innerhalb von 30 Tagen nach einem Löschantrag oder dem Widerruf der Einwilligung — je nachdem, was zuerst eintritt (siehe die Richtlinie zur Aufbewahrung biometrischer Daten).",
            "Rechtsgrundlage (GDPR Art. 9): Biometrische Daten sind eine besondere Kategorie und werden besonders geschützt: Sie werden nur verwendet, damit Personen ihre eigenen Fotos in dieser Galerie finden können. Als Verantwortlicher für die Teilnehmer entscheidet der Administrator der Galerie, ob die Gesichtssuche für seine Veranstaltung angemessen und am Veranstaltungsort zulässig ist. Wo das örtliche Recht die Einwilligung der fotografierten Personen verlangt, muss der Administrator sie haben, zum Beispiel über die Anmeldung zur Veranstaltung; hat er sie nicht, sollte er stattdessen die Suche nach Startnummer und Uhrzeit verwenden, die keine Gesichtsdaten nutzt. Photohawk stellt einen in das Produkt integrierten Einwilligungsschritt bereit: Ein Teilnehmer wird um seine Einwilligung gebeten, bevor sein Selfie für die Gesichtssuche verwendet wird, und kann ablehnen.",
            "Minderjährige: siehe §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Warum wir Daten nutzen und unsere Rechtsgrundlagen",
      blocks: [
        {
          type: "ul",
          items: [
            "Zur Bereitstellung des Dienstes (Galerien hosten, Suche betreiben, Bestellungen abwickeln): Erfüllung eines Vertrags.",
            "Zahlungen, Auszahlungen, Steuer- und Buchhaltungsunterlagen: Erfüllung eines Vertrags und Erfüllung einer rechtlichen Verpflichtung.",
            "Gesichts-/Selfie-Suche (Art. 9): die ausdrückliche Einwilligung der suchenden Person, bevor ihr Selfie verwendet wird; für die fotografierten Personen wie in §4 beschrieben.",
            "Marketing-E-Mails/-Nachrichten: nur mit Einwilligung des Empfängers (Opt-in), die jederzeit widerrufen werden kann.",
            "Analyse- und Werbe-Cookies: nur mit Ihrer Einwilligung, die über unser Cookie-Banner erteilt wird (siehe §12).",
            "Werbung bei ähnlichen Fachleuten (nur Kontoinhaber von Photohawk Studio): Wir können Meta die E-Mail-Adressen von Kontoinhabern in gehashter Form übermitteln, damit Meta unsere Anzeigen ähnlichen Fotografen und Veranstaltern zeigt und bestehende Kunden ausnimmt. Meta nutzt die gehashte Liste nur für diesen Abgleich und löscht sie danach. Rechtsgrundlage: unser berechtigtes Interesse. Sie können jederzeit widersprechen, indem Sie an support@photohawk.com schreiben; wir nehmen Sie dann aus. Galeriebesucher, Käufer und Teilnehmende sind nie enthalten.",
            "Sicherheit, Betrugsprävention und Verbesserung des Dienstes: unser berechtigtes Interesse, abgewogen gegen Ihre Rechte.",
          ],
        },
      ],
    },
    {
      heading: "6. Wo Ihre Daten gespeichert werden und internationale Übermittlungen",
      blocks: [
        {
          type: "ul",
          items: [
            "Datenspeicherort: Teilnehmer- und Kontodaten — einschließlich Fotos und biometrischer Gesichtsdaten — werden in der Europäischen Union gehostet (AWS, Irland / eu-west-1). Unser Identitätsdienst (AWS) befindet sich ebenfalls in der EU.",
            "Internationale Übermittlungen: Einige unserer Unterauftragsverarbeiter (siehe §7) sind außerhalb der EU/des Vereinigten Königreichs tätig (z. B. in den Vereinigten Staaten). Werden personenbezogene Daten außerhalb der EU/des Vereinigten Königreichs übermittelt, stützen wir uns auf einen Angemessenheitsbeschluss, sofern ein solcher gilt, und andernfalls auf die Standardvertragsklauseln (SCCs) der Europäischen Kommission zusammen mit dem UK International Data Transfer Addendum sowie erforderlichenfalls auf zusätzliche Garantien.",
          ],
        },
      ],
    },
    {
      heading: "7. Unterauftragsverarbeiter",
      blocks: [
        {
          type: "p",
          text: "Wir setzen eine kleine Zahl sorgfältig geprüfter Anbieter ein, um den Dienst bereitzustellen, jeweils auf Grundlage eines Auftragsverarbeitungsvertrags (und der SCCs/des UK Addendums, sofern sie Daten außerhalb der EU/des Vereinigten Königreichs verarbeiten):",
        },
        {
          type: "table",
          head: ["Anbieter", "Zweck", "Betroffene Daten"],
          rows: [
            ["Amazon Web Services (AWS)", "Hosting, Speicherung, Identität, Gesichtssuche, transaktionale E-Mails — EU-Region", "Alle personenbezogenen Daten, Fotos, biometrische Daten"],
            ["Cloudflare (inkl. R2)", "CDN, benutzerdefinierte Domains, Objektspeicher", "Fotos, Anfragedaten"],
            ["Stripe", "Kartenzahlungen und Abonnements", "Zahlungsdaten von Käufern und Kunden"],
            ["Xendit", "Zahlungen (Südostasien)", "Zahlungsdaten der Käufer"],
            ["Paystack", "Zahlungen (Afrika)", "Zahlungsdaten der Käufer"],
            ["Trolley", "Auszahlungen an Fotografen", "Identität und Bankverbindung des Zahlungsempfängers"],
            ["Postmark", "Transaktionale E-Mails", "Namen, E-Mail-Adressen"],
            ["Twilio / WhatsApp", "Fotoauslieferung und Benachrichtigungen", "Namen, Telefonnummern"],
            ["Google (Google Analytics)", "Website-Nutzungsanalyse (einwilligungsbasiert)", "Nutzungs-/Gerätedaten, Kennungen"],
            ["Meta Platforms (Meta Pixel und Conversions API)", "Werbung und Konversionsmessung (einwilligungsbasiert)", "Nutzungsdaten, Kennungen; mit Ihrer Einwilligung bei Registrierung, Anlegen eines Workspace, erstem Upload oder Kauf eines Tarifs: Ihre E-Mail-Adresse und Konto-ID (gehasht), IP-Adresse und Browsertyp"],
          ],
        },
        { type: "p", text: "Eine aktuelle Liste ist auf Anfrage erhältlich." },
      ],
    },
    {
      heading: "8. Wie lange wir Daten aufbewahren (Speicherfristen)",
      blocks: [
        {
          type: "ul",
          items: [
            "Gesichts-/biometrische Daten: aufbewahrt für den Zeitraum, den der Administrator der Galerie wählt (standardmäßig bis die Galerie, das Foto oder die Gesichtsdaten gelöscht werden), oder früher gelöscht, wie in §4 und in der Richtlinie zur Aufbewahrung biometrischer Daten beschrieben. Für die Suche verwendete Selfies werden nicht gespeichert.",
            "Fotos und Videos: Die Aufbewahrung wird vom Kontoinhaber (dem Veranstalter/Fotografen) gesteuert. Inhalte werden so lange aufbewahrt, wie der Kontoinhaber sie behält; Kontoinhaber können Fotos, Videos, Galerien und Teilnehmerdatensätze jederzeit löschen, und wir löschen sie, sobald sie dies tun. Photohawk legt für diese Inhalte keine feste Lebensdauer fest.",
            "Bestellungen und Finanzunterlagen: 7 Jahre aufbewahrt, um steuerliche und buchhalterische Pflichten zu erfüllen.",
            "Kontaktdatensätze von Teilnehmern/Käufern: vom Veranstalter gesteuert — aufbewahrt, solange der Veranstalter sie behält, und gelöscht, sobald der Veranstalter sie löscht oder auf ein verifiziertes Löschersuchen hin.",
            "Marketinglisten: aufbewahrt bis zum Widerruf der Einwilligung.",
            "Betriebs-/Sicherheitsprotokolle werden 30 Tage aufbewahrt.",
          ],
        },
      ],
    },
    {
      heading: "9. Wie wir Daten schützen",
      blocks: [
        {
          type: "ul",
          items: [
            "Bei der Übertragung: überall verschlüsselt über HTTPS/TLS.",
            "Im Ruhezustand: Fotos und Inhalte werden auf AWS und Cloudflare R2 gespeichert, die Daten im Ruhezustand standardmäßig verschlüsseln; sensible Zugangsdaten (z. B. Token verbundener Konten) werden zusätzlich mit AWS verschlüsselt.",
            "Zugriffskontrollen: authentifizierter Zugriff über AWS; der Zugriff von Mitarbeitern ist auf das für den Betrieb des Dienstes Erforderliche beschränkt.",
          ],
        },
      ],
    },
    {
      heading: "10. Ihre Rechte",
      blocks: [
        {
          type: "p",
          text: "Nach der GDPR/RGPD und der UK GDPR haben Sie das Recht: auf Auskunft über Ihre Daten; auf deren Berichtigung; auf deren Löschung („Recht auf Vergessenwerden“); auf Einschränkung der Verarbeitung oder Widerspruch dagegen; auf Datenübertragbarkeit; sowie darauf, Ihre Einwilligung jederzeit zu widerrufen. Sie haben außerdem das Recht, sich bei einer Aufsichtsbehörde zu beschweren (dem ICO im Vereinigten Königreich oder Ihrer örtlichen Behörde wie der CNIL in Frankreich).",
        },
        { type: "p", text: "So üben Sie sie aus:" },
        {
          type: "ul",
          items: [
            "Fotografen/Veranstalter (wir sind Verantwortlicher): Wenden Sie sich an unseren DPO unter support@photohawk.com. Wir antworten innerhalb eines Monats.",
            "Eventteilnehmer/Käufer (der Veranstalter ist Verantwortlicher): Wenden Sie sich bitte an den Fotografen oder Veranstalter, der Ihr Event durchgeführt hat. Fotografen können die personenbezogenen Daten eines Teilnehmers — einschließlich seiner Fotos und aller Gesichtssuchdaten — innerhalb von Photohawk löschen, und Photohawk führt ein verifiziertes Ersuchen im Auftrag des Veranstalters aus.",
            "Gesichtsdaten: Beantragen Sie die Löschung (oder widerrufen Sie Ihre Einwilligung) über „Anfrage stellen“ in der Selfie-Suche der Galerie oder unter support@photohawk.com. Wir bearbeiten den Antrag innerhalb von 30 Tagen.",
          ],
        },
        {
          type: "p",
          text: "Die Schließung eines Photohawk-Kontos beendet die Abrechnung, löscht aber nicht von sich aus die gespeicherten Daten; bitten Sie uns um die Löschung, falls Sie dies benötigen.",
        },
      ],
    },
    {
      heading: "11. Auftragsverarbeitungsvertrag (DPA)",
      blocks: [
        {
          type: "p",
          text: "Für Fotografen/Veranstalter unterliegt unsere Verarbeitung der Daten Ihrer Teilnehmer einem Auftragsverarbeitungsvertrag nach GDPR Art. 28, den wir auf Anfrage unter support@photohawk.com zur Verfügung stellen.",
        },
      ],
    },
    {
      heading: "12. Cookies und ähnliche Technologien",
      blocks: [
        {
          type: "ul",
          items: [
            "Unbedingt erforderliche Cookies (für Login, Sicherheit und Kernfunktionen) sind stets aktiv; sie erfordern keine Einwilligung.",
            "Analyse- und Werbe-Cookies — Google Analytics und der Meta Pixel (siehe §7) — sind nicht wesentlich und werden erst geladen, nachdem Sie über unser Cookie-Einwilligungsbanner zugestimmt haben. Sie können Ihre Wahl jederzeit über das Banner / die Cookie-Einstellungen ändern oder widerrufen. Eine Ablehnung lässt den Dienst uneingeschränkt nutzbar. Wenn Sie sich mit dieser Einwilligung registrieren, informieren unsere Server Meta zusätzlich (über dessen Conversions API), wenn Sie Ihr Konto und Ihren Workspace anlegen, zum ersten Mal Fotos hochladen oder einen Tarif kaufen. Übermittelt werden nur Ihre E-Mail-Adresse und Konto-ID in gehashter Form, Ihre IP-Adresse und Ihr Browsertyp. Eine Ablehnung oder ein Widerruf im Banner beendet auch dies.",
            "Weitere Einzelheiten sind in unserer separaten Cookie-Richtlinie dargelegt.",
          ],
        },
      ],
    },
    {
      heading: "13. Daten von Kindern",
      blocks: [
        {
          type: "p",
          text: "Events können Minderjährige einschließen. Ob die Gesichtssuche für eine Veranstaltung angemessen ist, entscheidet der Administrator der Galerie (siehe §4); wenn Minderjährige anwesend sein können und das örtliche Recht die Einwilligung der fotografierten Personen verlangt, muss der Administrator für sie die Einwilligung eines Elternteils oder Erziehungsberechtigten haben oder stattdessen die Suche nach Startnummer und Uhrzeit verwenden. Photohawk erstellt nicht wissentlich biometrische Daten über ein Kind ohne eine gesetzlich erforderliche Einwilligung, und der in das Produkt integrierte Einwilligungsschritt bekräftigt dies.",
        },
      ],
    },
    {
      heading: "14. Datenschutzverletzungen",
      blocks: [
        {
          type: "p",
          text: "Im Falle einer Verletzung des Schutzes personenbezogener Daten wird Photohawk den betroffenen Verantwortlichen unverzüglich nach Bekanntwerden benachrichtigen und ihn dabei unterstützen, seinen eigenen Meldepflichten gegenüber den Aufsichtsbehörden und den betroffenen Personen nachzukommen (GDPR Arts. 33–34).",
        },
      ],
    },
    {
      heading: "15. Änderungen dieser Richtlinie",
      blocks: [
        {
          type: "p",
          text: "Wir können diese Richtlinie aktualisieren; das oben angegebene „Datum des Inkrafttretens“ weist die aktuelle Version aus. Wesentliche Änderungen werden den Kontoinhabern mitgeteilt.",
        },
      ],
    },
    {
      heading: "16. Kontakt",
      blocks: [
        {
          type: "p",
          text: "Datenschutzbeauftragter — Andrew Hammond. CLOUDCONCUR LTD (Handelsregisternummer 13399913), tätig unter dem Namen Photohawk. E-Mail: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Vereinigtes Königreich.",
        },
      ],
    },
  ],
};

export default gdprDe;
